import { Controller } from "@hotwired/stimulus"
import Uppy from '@uppy/core'
import DragDrop from '@uppy/drag-drop'
import XHRUpload from '@uppy/xhr-upload'
import StatusBar from '@uppy/status-bar'

export default class extends Controller {
  static targets = ["container", "hiddenInput", "statusBar"]
  static values = {
    uploadPath: String,
    fileType: { type: String, default: "audio/*" }
  }

  connect() {
    this.initializeUppy();

    // Check for existing file data and update the preview
    // Required for invalid form submissions
    if (this.hasHiddenInputTarget && this.hiddenInputTarget.value) {
      try {
        const data = JSON.parse(this.hiddenInputTarget.value);
        if (data && data.id) {
          this.updatePreview(data);
        }
      } catch (e) {
        console.error("Error parsing file data", e);
      }
    }
  }

  initializeUppy() {
    // Get the CSRF token from the meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    this.uppy = new Uppy({
      id: `uppy-${this.element.id}`,
      autoProceed: true,
      restrictions: {
        maxFileSize: 20 * 1024 * 1024, // 20MB for audio files
        allowedFileTypes: [this.fileTypeValue]
      }
    })
    .use(DragDrop, {
      target: this.containerTarget
    });

    // Add StatusBar if target exists
    if (this.hasStatusBarTarget) {
      this.uppy.use(StatusBar, {
        target: this.statusBarTarget,
        hideUploadButton: true,
        hideAfterFinish: true
      });
    }

    this.uppy.use(XHRUpload, {
      endpoint: this.uploadPathValue || '/uploads',
      formData: true,
      headers: {
        'X-CSRF-Token': csrfToken
      }
    });

    this.uppy.on('upload-success', (file, response) => {
      const uploadedFileData = response.body;
      if (this.hasHiddenInputTarget) this.hiddenInputTarget.value = JSON.stringify(uploadedFileData);
      this.updatePreview(uploadedFileData, file.name);
    });

    this.uppy.on('upload-error', (file, error, response) => {
      console.error('Uppy upload error:', error, response);
    });
  }

  updatePreview(fileData, fileName = null) {
    if (!fileData) return;

    // Clear any existing content
    while (this.containerTarget.firstChild) {
      this.containerTarget.removeChild(this.containerTarget.firstChild);
    }

    // Create a simple file preview with the filename
    const fileUrl = `/uploads/${fileData.storage}/${fileData.id}`;
    const displayName = fileName || fileData.filename || `File-${fileData.id}`;

    const filePreview = document.createElement('div');
    filePreview.className = 'file-preview p-2';

    const fileIcon = document.createElement('span');
    fileIcon.className = 'file-icon mr-2';
    fileIcon.innerHTML = '🔊'; // Audio file icon

    const fileNameSpan = document.createElement('span');
    fileNameSpan.className = 'file-name text-sm font-medium text-gray-700';
    fileNameSpan.textContent = displayName;

    filePreview.appendChild(fileIcon);
    filePreview.appendChild(fileNameSpan);
    this.containerTarget.appendChild(filePreview);

    // Add the has-file class to the wrapper
    this.element.classList.add('has-file');
  }
}
